import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { SOURCES_DATA } from '../../static/data';
import Asset from '../../assets/icons/Asset';
import Connect from '../../assets/icons/Connect';
import Plus from '../../assets/icons/Plus';
import Window from '../../assets/icons/Window';
import { OnboardingStep, RootPathes, TabPathes, UserData } from '../../types';
import Step from './Step';
import Progress from './Progress';
import Button from '../ui/Button';
import ArrowsLine from '../../assets/icons/ArrowsLine';
import { completeUnboarding } from '../../api/user';
import Gear from '../../assets/icons/Gear';
import { getAssets } from '../../api/assets';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const TOTAL_STEPS = 5;

type Props = {
  user: UserData;
};

const Onboarding: FC<Props> = ({ user }) => {
  const { data } = useQuery({
    queryKey: ['media-assets'],
    queryFn: () => getAssets({ asset_type: 'media' }),
    staleTime: 1000 * 30,
  });

  const mediaAssets = data?.results.results || [];

  const stepsProgress = useMemo(() => {
    const connectedSources = SOURCES_DATA.filter(
      (source) => !!user?.[source.key as keyof UserData],
    );

    const preferencesSet = !!user?.preferencesSet;
    const hasSourceConnected = connectedSources.length > 0;
    const hasWalletConnected = !!user?.walletAddress;
    const hasAsset = mediaAssets.length > 0;
    const hasTwoAssets = mediaAssets.length > 1;

    const stepsCompleted = [
      preferencesSet,
      hasSourceConnected,
      hasWalletConnected,
      hasAsset,
      hasTwoAssets,
    ].filter(Boolean).length;

    return {
      preferencesSet,
      hasSourceConnected,
      hasWalletConnected,
      hasAsset,
      hasTwoAssets,
      stepsCompleted,
    };
  }, [user, mediaAssets]);

  const onboardingSteps: OnboardingStep[] = useMemo(
    () => [
      {
        id: 0,
        title: 'Set your preferernces',
        completed: stepsProgress.preferencesSet,
        url: RootPathes.CONFIG,
        description: 'Set your preferernces to complete',
        Icon: Gear,
      },
      {
        id: 1,
        title: 'Connect first data source',
        completed: stepsProgress.hasSourceConnected,
        url: `/${TabPathes.DATA}`,
        description: 'Connect your data resource to complete',
        Icon: Plus,
      },
      {
        id: 2,
        title: 'Connect your wallet',
        completed: stepsProgress.hasWalletConnected,
        url: `/${TabPathes.WALLET}`,
        description: 'Connect your crypto wallet to complete',
        Icon: Connect,
      },
      {
        id: 3,
        title: 'Create your first asset',
        completed: stepsProgress.hasAsset,
        url: `/${TabPathes.ASSETS}`,
        description: 'Upload your first asset to complete',
        Icon: Window,
      },
      {
        id: 4,
        title: 'Create 2 media asset',
        completed: stepsProgress.hasTwoAssets,
        url: `/${TabPathes.ASSETS}`,
        description: 'Upload media or art asset to complete',
        Icon: Asset,
      },
    ],
    [stepsProgress],
  );

  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState('0px');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      if (open) {
        setHeight(`${ref.current.scrollHeight}px`);
      } else {
        setHeight('0px');
      }
    }
  }, [open]);

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: completeUnboarding,
    onError: (error) => alert(`Error: ${error.message}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.invalidateQueries({ queryKey: ['wallet-info'] });
      await queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
    },
  });

  return (
    <div className={'flex flex-col gap-4'}>
      <Progress
        total={TOTAL_STEPS}
        ready={stepsProgress.stepsCompleted}
        text={`${stepsProgress.stepsCompleted}/${TOTAL_STEPS}`}
        onClick={() => setOpen(!open)}
      />

      {stepsProgress.stepsCompleted === onboardingSteps.length ? (
        <Button
          onClick={mutate}
          variant='solid'
          iconBack={<ArrowsLine color='currentColor' />}
          loading={isPending}
        >
          Receive 10 DAAC
        </Button>
      ) : (
        <div
          className={`
            flex flex-col gap-3 overflow-hidden transition-[max-height] duration-500 ease-in-out
            max-h-[${height}]
          `}
          ref={ref}
        >
          {onboardingSteps.map((step) => (
            <Step step={step} key={step.id} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Onboarding;
